import { buildApiPath } from '@/constants';
/**
 * 通用文件下载工具函数
 */

/**
 * 通过URL下载文件
 * @param url 文件URL
 * @param filename 下载文件名
 */
export const downloadFile = (url: string, filename: string): void => {
  try {
    // 创建一个隐藏的链接元素来触发下载
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    link.target = '_blank';

    // 添加到DOM，点击，然后移除
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('下载文件失败:', error);
    throw error;
  }
};

/**
 * 通过Blob下载文件
 * @param blob 文件Blob对象
 * @param filename 下载文件名
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  try {
    // 创建Blob URL
    const url = window.URL.createObjectURL(blob);

    // 下载文件
    downloadFile(url, filename);

    // 清理URL对象
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('下载Blob文件失败:', error);
    throw error;
  }
};

/**
 * 构建文件下载URL
 * @param fileId 文件ID
 * @param basePath 基础路径，默认为 '/files'
 * @returns 完整的下载URL
 */
export const buildDownloadUrl = (fileId: string | number, basePath: string = '/files'): string => {
  return `${window.location.origin}${buildApiPath(`${basePath}/${fileId}`)}`;
};

/**
 * 构建文件访问URL（用于图片显示等）
 * @param fileId 文件ID
 * @param basePath 基础路径，默认为 '/files'
 * @returns 完整的文件访问URL
 */
export const buildFileUrl = (fileId: string | number, basePath: string = '/files'): string => {
  return `${window.location.origin}${buildApiPath(`${basePath}/${fileId}`)}`;
};

/**
 * 使用 Office Online 预览 Word 文档
 * @param documentUrl 文档的完整 URL（需要公网可访问）
 * @returns Office Online 预览 URL
 */
export const getOfficeOnlinePreviewUrl = (documentUrl: string): string => {
  // Office Online 预览服务
  // 注意：文档 URL 必须是公网可访问的，且需要支持 CORS
  return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(documentUrl)}`;
};

/**
 * 使用 WPS 在线预览 Word 文档
 * @param documentUrl 文档的完整 URL（需要公网可访问）
 * @returns WPS 预览 URL
 */
export const getWpsPreviewUrl = (documentUrl: string): string => {
  // WPS 在线预览服务
  // 注意：文档 URL 必须是公网可访问的
  return `https://wwo.wps.cn/office/v1/view?url=${encodeURIComponent(documentUrl)}`;
};

/**
 * 判断 URL 是否为内网地址
 * @param url 要检查的 URL
 * @returns 如果是内网地址返回 true，否则返回 false
 */
export const isInternalNetworkUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // 检查是否为 localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return true;
    }

    // 检查是否为内网 IP 地址（192.168.x.x, 10.x.x.x, 172.16-31.x.x）
    const ipPattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
    const match = hostname.match(ipPattern);

    if (match) {
      const [, a, b, c, d] = match.map(Number);

      // 192.168.0.0 - 192.168.255.255
      if (a === 192 && b === 168) {
        return true;
      }

      // 10.0.0.0 - 10.255.255.255
      if (a === 10) {
        return true;
      }

      // 172.16.0.0 - 172.31.255.255
      if (a === 172 && b >= 16 && b <= 31) {
        return true;
      }
    }

    // 检查是否为 .local 域名
    if (hostname.endsWith('.local')) {
      return true;
    }

    return false;
  } catch (error) {
    // 如果 URL 解析失败，默认认为是内网地址（更安全）
    console.warn('URL 解析失败，默认认为是内网地址:', error);
    return true;
  }
};

/**
 * 使用第三方在线服务预览 Word 文档
 * @param documentUrl 文档的完整 URL
 * @param service 预览服务类型，默认为 'office'，可选 'office' | 'wps'
 * @returns 预览窗口的 Window 对象，如果打开失败或 URL 是内网地址则返回 null
 */
export const previewWordWithOnlineService = (
  documentUrl: string,
  service: 'office' | 'wps' = 'office'
): Window | null => {
  try {
    // 检查是否为内网地址，如果是则直接返回 null，不尝试第三方服务
    if (isInternalNetworkUrl(documentUrl)) {
      console.warn('文档 URL 是内网地址，第三方预览服务无法访问，跳过第三方服务');
      return null;
    }

    let previewUrl: string;

    if (service === 'office') {
      previewUrl = getOfficeOnlinePreviewUrl(documentUrl);
    } else {
      previewUrl = getWpsPreviewUrl(documentUrl);
    }

    // 打开新窗口预览
    const previewWindow = window.open(previewUrl, '_blank', 'width=1200,height=800');

    if (!previewWindow) {
      throw new Error('无法打开新窗口，请检查浏览器弹窗设置');
    }

    return previewWindow;
  } catch (error) {
    console.error('使用第三方服务预览失败:', error);
    return null;
  }
};
